import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import { Spin } from 'antd';

import AppLayout from '../components/layout/AppLayout';
import ProtectedRoute from '../components/layout/ProtectedRoute';
import LoginPage from '../pages/LoginPage';
import DashboardPage from '../pages/DashboardPage';
import NotFoundPage from '../pages/NotFoundPage';

// Після передеплою index.html у вже відкритій вкладці посилається на старі
// хеші чанків, яких на сервері вже немає → import() падає з "Failed to fetch
// dynamically imported module". Такий випадок лікується перезавантаженням, що
// підтягує свіжу версію. Перезавантажуємо один раз (вікно 10 с), щоб реальна
// мережева помилка не зациклила сторінку.
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /dynamically imported module|Importing a module script failed/i.test(msg);
}

function lazyWithReload<T extends ComponentType<unknown>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const KEY = 'chunk-reload-ts';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (isChunkLoadError(err) && Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise<{ default: T }>(() => {}); // ніколи не резолвиться — чекаємо reload
      }
      throw err;
    }
  });
}

const RoundPurchasesPage = lazyWithReload(() => import('../pages/purchases/RoundPurchasesPage'));
const RawPurchasesPage = lazyWithReload(() => import('../pages/purchases/RawPurchasesPage'));
const DryPurchasesPage = lazyWithReload(() => import('../pages/purchases/DryPurchasesPage'));
const HardwarePurchasesPage = lazyWithReload(
  () => import('../pages/purchases/HardwarePurchasesPage'),
);
const StockOverviewPage = lazyWithReload(() => import('../pages/stock/StockOverviewPage'));
const StockMovementsPage = lazyWithReload(() => import('../pages/stock/StockMovementsPage'));
const BlankDemandPage = lazyWithReload(() => import('../pages/stock/BlankDemandPage'));
const HardwareDemandPage = lazyWithReload(() => import('../pages/stock/HardwareDemandPage'));
const BlankStockPage = lazyWithReload(() => import('../pages/stock/BlankStockPage'));
const HardwareStockPage = lazyWithReload(() => import('../pages/stock/HardwareStockPage'));
const ProductStockPage = lazyWithReload(() => import('../pages/stock/ProductStockPage'));
const AdjustmentsPage = lazyWithReload(() => import('../pages/stock/AdjustmentsPage'));
const WasteStockPage = lazyWithReload(() => import('../pages/stock/WasteStockPage'));
const FirewoodPage = lazyWithReload(() => import('../pages/stock/FirewoodPage'));
const TransfersPage = lazyWithReload(() => import('../pages/stock/TransfersPage'));

// Пʼять цехів — це ОДНА сторінка (WorkshopModulePage) з параметром модуля;
// ці файли лише передають їй свій цех, тому й такі короткі.
const PrepModulePage = lazyWithReload(() => import('../pages/production/PrepModulePage'));
const FormatModulePage = lazyWithReload(() => import('../pages/production/FormatModulePage'));
const JoineryModulePage = lazyWithReload(() => import('../pages/production/JoineryModulePage'));
const ChairModulePage = lazyWithReload(() => import('../pages/production/ChairModulePage'));
const PackingModulePage = lazyWithReload(() => import('../pages/production/PackingModulePage'));

const DryingProcessesPage = lazyWithReload(() => import('../pages/drying/DryingProcessesPage'));
const ProcessingPage = lazyWithReload(() => import('../pages/processing/ProcessingPage'));
const SawingPage = lazyWithReload(() => import('../pages/sawing/SawingPage'));
const SortingPage = lazyWithReload(() => import('../pages/sorting/SortingPage'));
const AtmosphericDryingPage = lazyWithReload(
  () => import('../pages/atmospheric/AtmosphericDryingPage'),
);

const RoundTimberPage = lazyWithReload(() => import('../pages/references/RoundTimberPage'));
const RawMaterialsPage = lazyWithReload(() => import('../pages/references/RawMaterialsPage'));
const DryMaterialsPage = lazyWithReload(() => import('../pages/references/DryMaterialsPage'));
const BlanksPage = lazyWithReload(() => import('../pages/references/BlanksPage'));
const SuppliersPage = lazyWithReload(() => import('../pages/references/SuppliersPage'));
const CustomersPage = lazyWithReload(() => import('../pages/references/CustomersPage'));
const DryingChambersPage = lazyWithReload(() => import('../pages/references/DryingChambersPage'));
const ProductsPage = lazyWithReload(() => import('../pages/references/ProductsPage'));
const HardwarePage = lazyWithReload(() => import('../pages/references/HardwarePage'));
const WasteTypesPage = lazyWithReload(() => import('../pages/references/WasteTypesPage'));
const FinishesPage = lazyWithReload(() => import('../pages/references/FinishesPage'));
const ProductGradesPage = lazyWithReload(() => import('../pages/references/ProductGradesPage'));
const FabricsPage = lazyWithReload(() => import('../pages/references/FabricsPage'));
const MaterialsPage = lazyWithReload(() => import('../pages/references/MaterialsPage'));
const BoardSortClassesPage = lazyWithReload(
  () => import('../pages/references/BoardSortClassesPage'),
);

const ProductNormsPage = lazyWithReload(() => import('../pages/norms/ProductNormsPage'));
const CustomerOrdersPage = lazyWithReload(() => import('../pages/orders/CustomerOrdersPage'));
const MasterOrdersPage = lazyWithReload(() => import('../pages/orders/MasterOrdersPage'));
const MasterOrderDocPage = lazyWithReload(() => import('../pages/orders/MasterOrderDocPage'));
const ShipmentsPage = lazyWithReload(() => import('../pages/shipments/ShipmentsPage'));
const ClaimsPage = lazyWithReload(() => import('../pages/claims/ClaimsPage'));
const InvoicesPage = lazyWithReload(() => import('../pages/invoices/InvoicesPage'));
const UsersPage = lazyWithReload(() => import('../pages/users/UsersPage'));

const Fallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
    <Spin size="large" />
  </div>
);

const withSuspense = (Page: React.ComponentType) => (
  <Suspense fallback={<Fallback />}>
    <Page />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'references/round-timber', element: withSuspense(RoundTimberPage) },
      { path: 'references/raw-materials', element: withSuspense(RawMaterialsPage) },
      { path: 'references/dry-materials', element: withSuspense(DryMaterialsPage) },
      { path: 'references/blanks', element: withSuspense(BlanksPage) },
      { path: 'references/suppliers', element: withSuspense(SuppliersPage) },
      { path: 'references/customers', element: withSuspense(CustomersPage) },
      { path: 'references/drying-chambers', element: withSuspense(DryingChambersPage) },
      { path: 'references/products', element: withSuspense(ProductsPage) },
      { path: 'references/hardware', element: withSuspense(HardwarePage) },
      { path: 'references/waste-types', element: withSuspense(WasteTypesPage) },
      { path: 'references/finishes', element: withSuspense(FinishesPage) },
      { path: 'references/product-grades', element: withSuspense(ProductGradesPage) },
      { path: 'references/fabrics', element: withSuspense(FabricsPage) },
      { path: 'references/materials', element: withSuspense(MaterialsPage) },
      { path: 'references/board-sort-classes', element: withSuspense(BoardSortClassesPage) },
      { path: 'purchases/round', element: withSuspense(RoundPurchasesPage) },
      { path: 'purchases/raw', element: withSuspense(RawPurchasesPage) },
      { path: 'purchases/dry', element: withSuspense(DryPurchasesPage) },
      { path: 'purchases/hardware', element: withSuspense(HardwarePurchasesPage) },
      { path: 'warehouses/round-timber', element: withSuspense(StockOverviewPage) },
      { path: 'warehouses/predrying', element: withSuspense(StockOverviewPage) },
      { path: 'warehouses/sorting', element: withSuspense(StockOverviewPage) },
      { path: 'warehouses/dry', element: withSuspense(StockOverviewPage) },
      { path: 'warehouses/prep-shop', element: withSuspense(StockOverviewPage) },
      { path: 'warehouses/in-processing', element: withSuspense(StockOverviewPage) },
      { path: 'reports/movements', element: withSuspense(StockMovementsPage) },
      { path: 'warehouses/blank-demand', element: withSuspense(BlankDemandPage) },
      { path: 'warehouses/hardware-demand', element: withSuspense(HardwareDemandPage) },
      { path: 'warehouses/blanks', element: withSuspense(BlankStockPage) },
      { path: 'warehouses/products', element: withSuspense(ProductStockPage) },
      { path: 'warehouses/adjustments', element: withSuspense(AdjustmentsPage) },
      { path: 'warehouses/waste', element: withSuspense(WasteStockPage) },
      { path: 'warehouses/hardware', element: withSuspense(HardwareStockPage) },
      { path: 'warehouses/firewood', element: withSuspense(FirewoodPage) },
      { path: 'warehouses/transfers', element: withSuspense(TransfersPage) },
      { path: 'production/prep', element: withSuspense(PrepModulePage) },
      { path: 'production/format', element: withSuspense(FormatModulePage) },
      { path: 'production/joinery', element: withSuspense(JoineryModulePage) },
      { path: 'production/chair', element: withSuspense(ChairModulePage) },
      { path: 'production/packing', element: withSuspense(PackingModulePage) },
      { path: 'drying', element: withSuspense(DryingProcessesPage) },
      { path: 'atmospheric-drying', element: withSuspense(AtmosphericDryingPage) },
      { path: 'sorting', element: withSuspense(SortingPage) },
      { path: 'sawing', element: withSuspense(SawingPage) },
      { path: 'processing', element: withSuspense(ProcessingPage) },
      { path: 'norms', element: withSuspense(ProductNormsPage) },
      { path: 'orders', element: withSuspense(CustomerOrdersPage) },
      { path: 'master-orders', element: withSuspense(MasterOrdersPage) },
      { path: 'master-orders/:id', element: withSuspense(MasterOrderDocPage) },
      { path: 'shipments', element: withSuspense(ShipmentsPage) },
      { path: 'claims', element: withSuspense(ClaimsPage) },
      { path: 'invoices', element: withSuspense(InvoicesPage) },
      { path: 'users', element: withSuspense(UsersPage) },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
